import * as Sentry from '@sentry/nextjs'
import { Query, QueryClient } from '@tanstack/react-query'
import { QUERY_KEYS } from '@/constants/queryKeys'
import { isIdentifiedUser } from '@/constants/roles'
import { logout } from '@/services/logout'
import { IProfile } from '@/types'
import { isSessionRejected } from '@/utils/errors/sessionAuthFailure'
import { resetUserScopedQueries } from './resetUserScopedQueries'

type CachedQuery = Query<unknown, unknown, unknown>

// Our own title: a network failure throws "Failed to fetch", which Sentry's
// ignoreErrors drops before beforeSend; the original rides along as the cause.
const LOGOUT_FAILED_MESSAGE = 'Session end after a rejected token: logout failed'

// One session end per observed logged-in session. Armed at load rather than
// derived from the query holding logged-in data: an errored SSR prefetch is not
// dehydrated, so a rejection on a fresh page load arrives with no prior data
// and must still end the session. Without the latch, a Guest token rejected
// after our own reset would log out → refetch → reject in a loop.
let isSessionEndArmed = true

const isProfileQuery = (query: CachedQuery): boolean => query.queryKey[0] === QUERY_KEYS.profile[0]

export const endSessionOnAuthFailure = async (
  queryClient: QueryClient,
  error: unknown,
  query: CachedQuery,
): Promise<void> => {
  if (!isProfileQuery(query) || !isSessionRejected(error)) return
  if (!isSessionEndArmed) return
  isSessionEndArmed = false

  try {
    await logout()
  } catch (logoutError) {
    // The cookie may still hold a live session, so the client state stays as
    // is; the next rejection tries again.
    isSessionEndArmed = true
    Sentry.captureException(new Error(LOGOUT_FAILED_MESSAGE, { cause: logoutError }), { level: 'warning' })
    return
  }

  sessionStorage.clear()
  await resetUserScopedQueries(queryClient)
}

export const rearmSessionEndOnLogin = (data: unknown, query: CachedQuery): void => {
  if (!isProfileQuery(query)) return
  if (isIdentifiedUser((data as IProfile | undefined)?.rbac.role)) isSessionEndArmed = true
}
